import React from "react";
import DlReportScreen from "./DlReportScreen";
import { formatAmount, formatToISTDate } from "../../../utils/utils";

/**
 * The Daily Loan Outstanding report - what is still owed, and how much of it is
 * late.
 *
 * The server drops loans with nothing left to pay, so every row here has a
 * balance. Each figure comes from the same builder the Master report uses, so
 * the two reconcile by construction rather than by being checked afterwards.
 */
const columns = [
  { label: "Loan Acc No", render: (row) => row?.loanId || "-" },
  { label: "Customer Name", render: (row) => row?.memberName || "-" },
  { label: "Phone", render: (row) => row?.phoneNumber || "-" },
  { label: "Branch", render: (row) => row?.branchName || "-" },
  { label: "Employee", render: (row) => row?.employeeName || row?.username || "-" },

  { label: "Loan Amount", render: (row) => formatAmount(row?.loanAmount || 0) },
  { label: "Total Repayable", render: (row) => formatAmount(row?.totalRepayable || 0) },
  { label: "Daily Instalment", render: (row) => formatAmount(row?.dailyInstalment || 0) },
  {
    label: "Day Of Term",
    render: (row) =>
      row?.dayNumber != null ? `${row.dayNumber} of ${row.instalments}` : "-",
  },

  { label: "Days Collected", render: (row) => row?.emiDaysPaid ?? 0 },
  { label: "Total Collected", render: (row) => formatAmount(row?.totalEmiPaid || 0) },
  { label: "Principal Paid", render: (row) => formatAmount(row?.principalPaid || 0) },
  { label: "Interest Paid", render: (row) => formatAmount(row?.interestPaid || 0) },

  {
    label: "Outstanding Principal",
    render: (row) => formatAmount(row?.totalOutstandingPrincipal || 0),
  },
  {
    label: "Outstanding Interest",
    render: (row) => formatAmount(row?.totalOutstandingInterest || 0),
  },
  { label: "Overdue Principal", render: (row) => formatAmount(row?.overduePrincipal || 0) },
  { label: "Overdue Interest", render: (row) => formatAmount(row?.overdueInterest || 0) },
  { label: "PAR", render: (row) => formatAmount(row?.totalPAR || 0) },
  { label: "DPD", render: (row) => row?.dpd ?? 0 },
  { label: "Bucket", render: (row) => row?.bucket || "-" },
  {
    label: "First Overdue",
    render: (row) =>
      row?.firstOverdueDate ? formatToISTDate(row.firstOverdueDate) : "-",
  },
  {
    label: "Last Paid Date",
    render: (row) => (row?.lastPaidDate ? formatToISTDate(row.lastPaidDate) : "-"),
  },
];

/**
 * The portfolio totals. Summed over the rows on screen rather than taken from
 * the response, so the figures always add up to what the user is looking at
 * after they have filtered by branch or officer.
 */
const renderSummary = (rows) => {
  const sum = (key) => rows.reduce((total, row) => total + (row?.[key] || 0), 0);
  const inArrears = rows.filter((row) => (row?.totalPAR || 0) > 0).length;

  const tiles = [
    { label: "Loans", value: rows.length },
    { label: "Disbursed", value: formatAmount(sum("loanAmount")) },
    { label: "Collected", value: formatAmount(sum("totalEmiPaid")) },
    {
      label: "Outstanding Principal",
      value: formatAmount(sum("totalOutstandingPrincipal")),
    },
    { label: "PAR", value: formatAmount(sum("totalPAR")) },
    { label: "Loans In Arrears", value: inArrears },
  ];

  return (
    <div className="d-flex flex-wrap gap-3 px-3 pb-2">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="border rounded p-2 px-3"
          style={{ minWidth: 150 }}
        >
          <div style={{ fontSize: 11, color: "#777" }}>{tile.label}</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>{tile.value}</div>
        </div>
      ))}
    </div>
  );
};

const DlOutstandingReport = () => (
  <DlReportScreen
    title="DL Outstanding Report"
    endpoint="getDlOutstandingReport"
    reportName="DlOutstandingReport"
    columns={columns}
    renderSummary={renderSummary}
  />
);

export default DlOutstandingReport;
